import { PortableTextBlock } from '@portabletext/types'
import { bind } from '@react-rxjs/core'
import { switchMap } from 'rxjs'

import { SanityDispensaryDocument } from '../../../groq/sanity-dispensaries/types'
import { mapRight } from '../../../utils/rx/operators'
import { FetchRight } from '../../fetch'
import { dispensariesSiteSettingsCdn$, sanityDispensaryCdn$ } from '../../sanity-dispensaries/query'
import { latestDispensarySlug$ } from '../../sanity-dispensaries/state'

const dispensaryDefaultValues$ = dispensariesSiteSettingsCdn$.pipe(
  mapRight(
    (siteSettingsResult): FetchRight<Partial<SanityDispensaryDocument>> => ({
      _tag: 'Right' as const,
      data: {
        bio: {
          _type: 'bio',
          content: siteSettingsResult.data?.defaultDispensaryBio || null,
          //Bio image is now called hero image
          image: siteSettingsResult.data?.defaultDispensaryBioImage || null,
        },
        logo: siteSettingsResult.data?.defaultDispensaryLogo
          ? { _type: 'logo', image: siteSettingsResult.data.defaultDispensaryLogo }
          : null,
        homePage: {
          _type: 'homePage',
          promotionalBanner: siteSettingsResult.data?.defaultDispensaryPromotionalBanner
            ? {
                _type: 'reference',
                _ref: siteSettingsResult.data.defaultDispensaryPromotionalBanner._id,
              }
            : null,
          heroText: null,
          isCustomBanner: null,
          customBannerLayout: null,
          customBannerColor: null,
          customBannerProducts: null,
        },
      },
    }),
  ),
)

function filterAndTakeUnlessEmpty<T extends PortableTextBlock>(
  blocks: ReadonlyArray<T> | null | undefined,
): Array<T> | undefined {
  // TODO: should we trim empty blocks from leading and trailing edges only?
  const filtered = blocks?.filter(
    (block) => !(block.children.length === 1 && block.children[0].text === '\n'),
  )
  return filtered && filtered.length ? filtered : undefined
}

const dispensaryValuesWithDefault$ = sanityDispensaryCdn$.pipe(
  switchMap((dispensaryValue) => {
    if (dispensaryValue._tag === 'Left') return dispensaryDefaultValues$
    return dispensaryDefaultValues$.pipe(
      mapRight((defaultValue) => {
        return {
          _tag: 'Right' as const,
          data: {
            ...dispensaryValue.data,
            bio: {
              content:
                filterAndTakeUnlessEmpty(dispensaryValue.data?.bio?.content) ||
                defaultValue.data?.bio?.content,
              image: dispensaryValue.data?.bio?.image ?? defaultValue.data?.bio?.image,
            },
            logo: dispensaryValue.data?.logo ?? defaultValue.data?.logo,
          },
        }
      }),
    )
  }),
)

export const [useDispensaryOrDefaultValues, latestDispensaryOrDefaultValues$] = bind(
  latestDispensarySlug$.pipe(
    switchMap((dispensarySlug) =>
      dispensarySlug ? dispensaryValuesWithDefault$ : dispensaryDefaultValues$,
    ),
  ),
)
